export type PricedEntity = {
  price: number;
  salePrice?: number;
  mrp?: number;
  promoPrice?: number | null;
  promoStartsAt?: string | null;
  promoEndsAt?: string | null;
};

/** A scheduled sale is live: promo price set and now is inside its window */
export const isPromoActive = (item: PricedEntity, now: Date = new Date()): boolean => {
  if (item.promoPrice == null || item.promoPrice <= 0) return false;
  if (item.promoStartsAt && now < new Date(item.promoStartsAt)) return false;
  if (item.promoEndsAt && now > new Date(item.promoEndsAt)) return false;
  return true;
};

export const getEffectivePrice = (item: PricedEntity): number =>
  isPromoActive(item) ? (item.promoPrice as number) : item.salePrice ?? item.price;

export const getMrp = (item: PricedEntity): number =>
  item.mrp ?? getEffectivePrice(item);

export const hasDiscount = (item: PricedEntity): boolean =>
  getMrp(item) > getEffectivePrice(item);

export const getSavingsAmount = (item: PricedEntity): number => {
  const mrp = getMrp(item);
  const effectivePrice = getEffectivePrice(item);
  return Math.max(0, mrp - effectivePrice);
};

export const getDiscountPercent = (item: PricedEntity): number => {
  const effectivePrice = getEffectivePrice(item);
  const mrp = getMrp(item);
  if (mrp <= 0 || mrp <= effectivePrice) return 0;
  return Math.round(((mrp - effectivePrice) / mrp) * 100);
};

export const formatCurrency = (value: number): string =>
  `₹${new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)}`;
